'use client';

import React, { useEffect, useRef } from 'react';
import StackedHero from '@/components/our-work/StackedHero';

const caseStudies = [
    {
        heading: 'Aaramgram Resort – Hospitality Marketing Success',
        body: 'For Aaramgram Resort, our objective was to increase room occupancy and drive direct booking enquiries during the peak travel season. Through a highly targeted Meta advertising strategy and optimized audience segmentation, we successfully generated consistent booking enquiries that helped fill room inventory for the next two months. Achieving these results with a modest daily budget demonstrates our ability to maximize ROI and deliver tangible business outcomes for hospitality brands.',
        images: ['/images/our-work/meta-aaramgram-ads.webp'],
    },
    {
        heading: 'Design1Studio – Brand Growth & Lead Generation Strategy',
        body: 'For Design1Studio Interiors, we implemented a two-stage growth strategy combining brand awareness with lead generation. Our video engagement campaign generated over 1 million video views, significantly increasing brand visibility and audience engagement while building a large pool of highly relevant prospects. We then leveraged this engaged audience through strategic retargeting campaigns, generating qualified interior design enquiries from users who had already interacted with the brand. This full-funnel approach showcases our expertise in turning attention into business opportunities.',
        images: ['/images/our-work/meta-d1s-ads-1.webp', '/images/our-work/meta-d1s-ads-2.webp'],
    },
    {
        heading: 'Koza Aesthetic Care – High-Intent Healthcare Lead Generation',
        body: 'For Koza Aesthetic Care, our campaigns focused on attracting high-intent prospects for aesthetic and cosmetic treatments. Within just two weeks, our treatment-focused campaigns generated 201 qualified enquiries, creating a strong pipeline of potential patients. Additionally, our specialized hair transplant campaign delivered 21 quality leads in only three days at an efficient acquisition cost, demonstrating our ability to generate demand for high-ticket healthcare services through precise audience targeting, compelling creatives, and performance-driven optimization.',
        images: ['/images/our-work/meta-koza-ads.webp'],
    },
];

export default function MetaAdsPage() {
    const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        document.title = 'Meta Ads & Performance Marketing | Lead Workz';
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('meta-in');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08 }
        );
        imageRefs.current.forEach((el) => { if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <style>{`
                @keyframes meta-rise {
                    from { opacity: 0; transform: translateY(48px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .meta-anim { opacity: 0; }
                .meta-anim.meta-in { animation: meta-rise 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }
                @media (max-width: 900px) {
                    .meta-content { padding: 44px 20px 80px !important; gap: 56px !important; }
                    .meta-case-heading { font-size: clamp(20px, 5vw, 32px) !important; letter-spacing: 0.02em !important; }
                    .meta-case-body { font-size: 14px !important; line-height: 1.7 !important; letter-spacing: 0.03em !important; }
                    .meta-img { border-radius: 24px !important; }
                }
            `}</style>

            <main style={{ background: '#0f0f0f', minHeight: '100vh', overflow: 'clip' }}>

                <StackedHero
                    title={<>Meta Ads &amp; Performance Marketing</>}
                    description="We create and manage high-performing Meta ad campaigns that are built to deliver results. From audience targeting to creative strategy and optimization, we focus on driving quality leads, conversions, and measurable growth."
                >

                    {/* ── Case studies ── */}
                    <div
                        className="meta-content"
                        style={{
                            maxWidth: 1626,
                            margin: '0 auto',
                            padding: 'clamp(56px, 6vw, 96px) clamp(20px, 3.5vw, 56px) 120px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'clamp(60px, 7vw, 100px)',
                        }}
                    >
                        {caseStudies.map((study, si) => {
                            const refStart = caseStudies.slice(0, si).reduce((acc, s) => acc + s.images.length, 0);
                            return (
                                <div key={si} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(24px, 3vw, 44px)' }}>
                                    {/* Text block */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.5vw, 20px)' }}>
                                        <h2
                                            className="meta-case-heading"
                                            style={{
                                                fontFamily: '"Clash Display", sans-serif',
                                                fontWeight: 700,
                                                fontSize: 'clamp(20px, 2.8vw, 42px)',
                                                background: 'linear-gradient(90deg, #3730a3 0%, #080400 100%)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                letterSpacing: '0.02em',
                                                lineHeight: 1.25,
                                                margin: 0,
                                            }}
                                        >
                                            {study.heading}
                                        </h2>
                                        <p
                                            className="meta-case-body"
                                            style={{
                                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                                fontWeight: 400,
                                                fontSize: 'clamp(14px, 1.2vw, 18px)',
                                                color: '#080400',
                                                letterSpacing: '0.04em',
                                                lineHeight: 1.8,
                                                margin: 0,
                                            }}
                                        >
                                            {study.body}
                                        </p>
                                    </div>

                                    {/* Images */}
                                    {study.images.map((src, ii) => (
                                        <div
                                            key={ii}
                                            ref={el => { imageRefs.current[refStart + ii] = el; }}
                                            className="meta-anim meta-img"
                                            style={{
                                                borderRadius: 'clamp(20px, 4vw, 64px)',
                                                overflow: 'hidden',
                                                background: '#e8e5df',
                                                width: '100%',
                                            }}
                                        >
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={src}
                                                alt={study.heading}
                                                style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </StackedHero>

            </main>
        </>
    );
}
